import { fetchUnreviewedReports } from '@/entities/inbox';
import { isC1cStudy } from '@/entities/study';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import {
  emitter,
  showMessageError,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import type { InboxSocketMessage } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatSortValue } from '../../../lib/inboxSearch';
import { formatInstantReports } from '../../../lib/instantInboxList';
import type { InstantInboxListProps, InstantInboxListState } from './instantInboxList.types';

/**
 * Drives the instant, unreviewed inbox tab: filter state, cursor paging, facility and see-patients scoping and row navigation. Seeds a filter ref from the search-goto field, value, physician and sort selection, re-queries whenever the tab becomes active or the facility or patient scope changes, exposes handleReloadData and fetchData through the imperative ref, and patches loaded rows from INBOX_EVENTS socket messages.
 *
 * @param isActive - True while the instant inbox tab is visible; gates fetching and search changes.
 * @param totalInboxCount - Total unreviewed report count used to decide when paging reached the end.
 * @param searchGoto - Search-bar state supplying the search field, value, physician and sort selection.
 * @param selectedFacilityProp - Facility filter selection; the all-facilities sentinel clears the filter and shows facility names.
 * @param selectedTypeSeePatients - Patient-scope selection deciding whether viewAll is sent.
 * @param ref - Imperative handle receiving handleReloadData and fetchData.
 * @returns List state together with the filter ref, page size and the search, paging and row-click handlers.
 */
const useInstantInboxList = (
  {
    isActive,
    totalInboxCount,
    searchGoto,
    selectedFacility: selectedFacilityProp,
    selectedTypeSeePatients,
  }: InstantInboxListProps,
  ref: any,
) => {
  const navigate = useNavigate();

  const isSearchApply = useRef(false);
  const filterObject = useRef({
    studyFid:
      searchGoto.searchByValue === 'studyFid'
        ? searchGoto.searchValue?.toString()?.trim() &&
          !_.isNaN(parseInt(searchGoto.searchValue, 10))
          ? parseInt(searchGoto.searchValue, 10)
          : undefined
        : undefined,
    patientName: searchGoto.searchByValue === 'patientName' ? searchGoto.searchValue : undefined,
    patientId: searchGoto.searchByValue === 'patientId' ? searchGoto.searchValue : undefined,
    physicianId:
      searchGoto.searchByValue === 'physicianId'
        ? searchGoto.physicianSearchValue?.value
        : undefined,
    sortOrder: formatSortValue(searchGoto.sortValue).sortOrder,
    reportStatus: 'Generated',
    lastModifiedCursor: undefined,
    sortBy: 'lastModified',
    facilityId:
      selectedFacilityProp.value !== GETTEXT_ALL_FACILITIES
        ? selectedFacilityProp.value
        : undefined,
    viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
  });
  const sizePerPage = useRef(10);
  const needFetchMoreInboxes = useRef(false);
  const needFetchDataWithNewFacility = useRef(false);
  const selectedFacility = useRef(_.cloneDeep(selectedFacilityProp));

  const [state, setState] = useMergeState<InstantInboxListState>({
    skeletonInboxes: _.map(_.range(10), () => ({})),
    inboxes: [],
    isLoadedData: false,
    pageIndex: 0,
    isEndOfData: false,
    isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
  });

  /**
   * Loads a page of unreviewed instant reports using the accumulated inbox filter. Refreshes facilityId from the selected facility, merges the overrides into the persistent filter ref, coerces studyFid to an integer and sets viewAll, then appends the formatted rows and advances the page index when a lastModifiedCursor is set or replaces them at page zero, deriving isEndOfData from the returned count and totalInboxCount; failures show the error and leave an empty list.
   *
   * @param updatedFilterObject - Filter fields merged into the persistent filter before querying.
   * @param isFetchMoreByClickingNext - True when paging forward, which advances the page index and clears the fetch-more flag.
   * @param limit - Maximum number of rows to request.
   * @returns A promise that settles once the rows have been stored or the failure handled.
   */
  const fetchData = async (
    updatedFilterObject = {},
    isFetchMoreByClickingNext = false,
    limit = 10,
  ) => {
    filterObject.current.facilityId =
      selectedFacilityProp.value !== GETTEXT_ALL_FACILITIES
        ? selectedFacilityProp.value
        : undefined;

    const queryFilter = { ...filterObject.current, ...updatedFilterObject };
    filterObject.current = { ...queryFilter };
    try {
      const queryFilterPayload = {
        ...queryFilter,
        studyFid:
          queryFilter?.studyFid?.toString()?.trim() &&
          !_.isNaN(parseInt(queryFilter?.studyFid as unknown as string, 10))
            ? parseInt(queryFilter?.studyFid as unknown as string, 10)
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
      };
      const result = await fetchUnreviewedReports(
        _.omit(queryFilterPayload, 'statusReports'),
        limit,
      );
      const newInboxes = formatInstantReports(_.compact(result));

      const setStateObject = {};

      if (queryFilter.lastModifiedCursor) {
        const concatenatedInboxes = [...state.inboxes, ...newInboxes];
        _.assign(setStateObject, {
          inboxes: concatenatedInboxes,
          pageIndex:
            isFetchMoreByClickingNext && !!newInboxes.length
              ? state.pageIndex + 1
              : state.pageIndex,
          isEndOfData: newInboxes.length < 10 || concatenatedInboxes.length === totalInboxCount,
          isLoadedData: true,
        });
      } else {
        _.assign(setStateObject, {
          inboxes: newInboxes,
          pageIndex: 0,
          isEndOfData: newInboxes.length < 10 || newInboxes.length === totalInboxCount,
          isLoadedData: true,
        });
      }

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch inboxes: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        inboxes: [],
        isLoadedData: true,
      });
    }

    if (isFetchMoreByClickingNext) {
      needFetchMoreInboxes.current = false;
    }
  };

  /**
   * Applies a change from the instant inbox search bar to the list. Only acts while the tab is active: it merges the new filter fields into the persistent filter, forces priority to undefined and sortBy to lastModified, then re-queries and shows the skeleton rows.
   *
   * @param updatedFilterObject - Filter fields produced by the search component.
   * @returns Nothing.
   */
  const onChangeSearchComponent = (updatedFilterObject: any) => {
    if (isActive) {
      _.assign(filterObject.current, { ...updatedFilterObject });
      _.assign(filterObject.current, { priority: undefined, sortBy: 'lastModified' });
      fetchData(filterObject.current, needFetchMoreInboxes.current);
      setState({ isLoadedData: false });
    }
  };

  /**
   * Moves the instant inbox list to another page, fetching more rows when that page is not buffered yet. When the requested page runs past the loaded rows and the end of data has not been reached it raises the fetch-more flag, sets lastModifiedCursor to the last loaded row's lastModified and re-queries while showing the skeleton; otherwise it only stores the new page index.
   *
   * @param newPageIndex - Zero-based page index to display.
   * @returns Nothing.
   */
  const navigatePage = (newPageIndex: number) => {
    if (newPageIndex * 10 + 10 >= state.inboxes.length && !state.isEndOfData) {
      needFetchMoreInboxes.current = true;

      filterObject.current.lastModifiedCursor =
        state.inboxes[state.inboxes.length - 1].lastModified;

      fetchData(filterObject.current, needFetchMoreInboxes.current);
      setState({ isLoadedData: false });
    } else {
      setState({ pageIndex: newPageIndex });
    }
  };

  /**
   * Reloads the instant inbox list from the first page. Lowers isLoadedData to show the skeleton rows and re-queries with lastModifiedCursor cleared and paging disabled.
   *
   * @returns Nothing.
   */
  const handleReloadData = () => {
    setState({ isLoadedData: false });
    fetchData({ lastModifiedCursor: undefined }, false);
  };

  useImperativeHandle(ref, () => ({
    handleReloadData,
    fetchData,
  }));

  /**
   * Opens the detail page of an instant report row the user clicked. A report of a CardioOne study raises the CardioOne report notice; any other report navigates to /inbox/`<report id>`.
   *
   * @param report - Clicked report row supplying the id in the route and its study.
   * @returns Nothing.
   */
  const onClickReport = (report: {
    id: string;
    study?: Parameters<typeof isC1cStudy>[0] | null;
  }) => {
    // The backend never creates instant reports for c1c studies; this is only an extra frontend guard.
    if (isC1cStudy(report.study ?? {})) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      return;
    }
    navigate(`/inbox/${report.id}`);
  };

  useEffect(() => {
    if (isSearchApply.current || needFetchDataWithNewFacility.current) {
      isSearchApply.current = false;
      needFetchDataWithNewFacility.current = false;
      setState({ isLoadedData: false });
      fetchData({ lastModifiedCursor: undefined });
    } else if (isActive && !state.isEndOfData) {
      setState({ isLoadedData: false });
      fetchData({ lastModifiedCursor: undefined });
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      isSearchApply.current = true;
      const { searchByValue, searchValue, physicianSearchValue, sortValue } = searchGoto;
      const dataFilter: any = {
        patientName: undefined,
        patientId: undefined,
        studyFid: undefined,
        physicianId: undefined,
        sortOrder: formatSortValue(sortValue).sortOrder,
      };
      _.forEach(['patientName', 'patientId', 'studyFid', 'physicianId'], (d) => {
        if (searchByValue === d) {
          if (searchByValue === 'physicianId') {
            dataFilter.physicianId = physicianSearchValue?.value;
          } else if (searchByValue === 'studyFid') {
            dataFilter.studyFid =
              searchValue?.toString().trim() && !_.isNaN(parseInt(searchValue, 10))
                ? parseInt(searchValue, 10)
                : undefined;
          } else {
            dataFilter[d] = searchValue;
          }
        }
      });
      filterObject.current = _.assign({ ...filterObject.current }, dataFilter);
    }
  }, [searchGoto]);

  useEffect(() => {
    setState({
      isEndOfData: state.inboxes.length < 10 || state.inboxes.length === totalInboxCount,
    });
  }, [totalInboxCount, state.inboxes]);

  useUpdateEffect(() => {
    if (!_.isEqual(selectedFacility.current, selectedFacilityProp)) {
      setState({
        isLoadedData: false,
        isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
      });
      selectedFacility.current = _.cloneDeep(selectedFacilityProp);
      if (isActive) {
        fetchData({ lastModifiedCursor: undefined });
      } else {
        needFetchDataWithNewFacility.current = true;
      }
    }
  }, [selectedFacilityProp?.value]);

  useUpdateEffect(() => {
    setState({ isLoadedData: false });
    fetchData();
  }, [selectedTypeSeePatients?.value]);

  /**
   * Updates an instant report row when its end-of-use study report is sent or recalled. Acts only on messages of type 'Study': it deep clones the loaded rows, finds the instant report owning a child report with the message id, refreshes that child's isNewFormat and physicianInterpretation when supplied, and sets its inbox date to null on recall or to the message date otherwise.
   *
   * @param msg - Inbox socket message describing the updated report.
   * @returns A promise that settles once the affected row has been patched or skipped.
   */
  const handleInboxSocket = async (msg: InboxSocketMessage) => {
    // *: Update the EoU report's send status to display the View Verified Report modal
    if (msg.type === 'Study') {
      const clonedInboxes = _.cloneDeep(state.inboxes);
      /**
       * Picks the loaded instant report that owns the report referenced by the socket message. Scans the cloned inbox rows for the first one whose reports array contains an entry with the message id.
       *
       * @returns The matching instant report row, or undefined when the message targets no loaded row.
       */
      const foundInstantReport = clonedInboxes.find(
        (x) => !!x.reports?.find((y: any) => y.id === msg.id),
      );
      if (!foundInstantReport) return;
      /**
       * Picks the end-of-use child report targeted by the inbox socket message. Searches the owning instant report's reports array for the entry whose id matches the message id.
       *
       * @returns The matching child report, or undefined when it is absent.
       */
      const foundEouReport = foundInstantReport.reports?.find((x: any) => x.id === msg.id);
      foundEouReport.isNewFormat = !_.isNil(msg.isNewFormat)
        ? msg.isNewFormat
        : foundEouReport.isNewFormat;
      foundEouReport.physicianInterpretation = !_.isNil(msg.physicianInterpretation)
        ? msg.physicianInterpretation
        : foundEouReport.physicianInterpretation;
      Object.assign(foundEouReport.inbox, { date: msg.recalled ? null : msg.inbox.date });
      setState({ inboxes: clonedInboxes });
    }
  };

  useEmitter(EMITTER_CONSTANTS.INBOX_EVENTS, handleInboxSocket, [state.inboxes]);

  return {
    state,
    setState,
    navigate,
    filterObject,
    sizePerPage,
    onChangeSearchComponent,
    navigatePage,
    onClickReport,
  };
};

export { useInstantInboxList };
